import React, { useEffect, useState } from 'react';
import { useWidget } from 'components/Widget/useWidget';
import ninjaApi from 'store/redux/apis';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import WidgetCard from 'components/widgets/components/WidgetCard';
import WidgetCharts from 'components/widgets/components/charts/WidgetCharts';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import { mdiCashMultiple, mdiCash, mdiBank, mdiAccountGroup, mdiBullseyeArrow } from '@mdi/js';
import getFormattedValue from 'store/redux/utils/getFormattedValue';
import NoResultFound from 'components/charts/components/NoResultFound';
import { ValueDotFormatter } from 'components/tables/NinjaTable/formatters';
import { RoiDefinition, RoasDefinition } from 'components/NameDefinition';
import { RoiFormatter, RoasFormatter } from 'components/tables/NinjaTable/formatters';
import { postbackMetrics } from './Postbackmetrics';
import PostbackActionFormatter from 'modules/PostbacksModule/formatters/PostbackActionFormatter';

const PostbackReports = ({ widget_key, height, data }) => {
  const [getDashboardPostbackReports, { data: postbacks = [], isLoading, isFetching }] =
    ninjaApi.useLazyDashboardPostbackReportsQuery();
  const { filters, chartConfig } = useWidget();
  const { date_from, date_to } = useWidgetDate(widget_key);
  const [isChartLoading, setIsChartLoading] = useState(false);

  useEffect(() => {
    if (chartConfig?.load_chart) {
      setIsChartLoading(true);
      const timer = setTimeout(() => {
        setIsChartLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [chartConfig?.load_chart]);

  useEffect(() => {
    const params = {
      load_chart: chartConfig?.load_chart,
      date_from: date_from,
      date_to: date_to,
      ...filters,
    };

    if (chartConfig?.load_chart) {
      params.group_by = 'date';
    }

    if (params?.date_from) {
      getDashboardPostbackReports(params).unwrap();
    }
  }, [filters, chartConfig?.load_chart, data, widget_key]);

  if (!postbacks?.data?.length > 0) {
    return (
      <NoResultFound icon={false} height={height} title='No data found for this date range.' />
    );
  }

  const metricsArray = [
    {
      key: 'ad_spent',
      label: 'Ad Spent',
      icon: mdiCash,
      definition: { name: 'Ad Spent', description: 'All Ads Spent with Postbacks' },
      value: getFormattedValue(postbacks?.totals[0]?.ad_spent),
      exctraAction: (
        <PostbackActionFormatter
          disableDeleteAction={true}
          ids={
            data?.defaultFilter?.filters?.postback_ids?.length
              ? data?.defaultFilter?.filters?.postback_ids
              : 'all'
          }
        />
      ),
    },
    {
      key: 'gross_income',
      icon: mdiCashMultiple,
      label: 'Gross Income',
      definition: { name: 'Gross Income', description: 'Gross Revenue from the sales' },
      value: <ValueDotFormatter value={postbacks?.totals[0]?.gross_income} />,
    },
    {
      key: 'net_profit',
      icon: mdiBank,
      label: 'Net Profit',
      definition: { name: 'Net Profit', description: 'Gross Profit - Ad Spent' },
      value: getFormattedValue(postbacks?.totals[0]?.net_profit),
    },
    {
      key: 'roas',
      existDefinition: <RoasDefinition showIcon />,
      label: 'ROAS',
      formatter: () => <RoasFormatter row={postbacks?.totals[0]} fontSize={18} />,
    },
    {
      key: 'roi',
      existDefinition: <RoiDefinition showIcon />,
      label: 'ROI',
      formatter: () => <RoiFormatter row={postbacks?.totals[0]} fontSize={18} />,
    },
    {
      key: 'cpa',
      icon: mdiBullseyeArrow,
      definition: { name: 'CPA', description: 'CPA Count' },
      value: getFormattedNumber(postbacks?.totals[0]?.cpa),
    },
    {
      key: 'cpl',
      icon: mdiAccountGroup,
      definition: { name: 'CPL', description: 'CPL Count' },
      value: getFormattedNumber(postbacks?.totals[0]?.cpl),
    },
    {
      key: 'count',
      icon: mdiCashMultiple,
      definition: {
        name: 'Count',
        description: 'Number of received postbacks',
      },
      value: getFormattedNumber(postbacks?.totals[0]?.count),
    },
  ];

  return (
    <div
      style={{
        height: height - 50,
      }}
      className=' h-full grid gap-2 m-auto items-center w-full md:grid-cols-[repeat(auto-fit,minmax(250px,1fr))] grid-cols-1'
    >
      {metricsArray.map((metric, index, key) => (
        <>
          {chartConfig?.load_chart ? (
            <WidgetCharts
              data={postbacks.data}
              index={metric.key}
              totals={postbacks?.totals[0]}
              loading={isLoading || isChartLoading || isFetching}
              metricList={postbackMetrics}
              {...metric}
            />
          ) : (
            <WidgetCard
              loading={isLoading || isChartLoading || isFetching}
              key={index}
              {...metric}
            />
          )}
        </>
      ))}
    </div>
  );
};

export default PostbackReports;
